import { IMAGES } from "../../lib/site";
import { Reveal } from "./motion";
import { VideoPlaceholder, Swoosh } from "./ui";

// ---- Vistas (green background) ----
export const Vistas = () => (
  <section id="vistas" className="relative overflow-hidden bg-[#2E9B77] py-24 lg:py-32">
    <Swoosh className="-right-32 -top-10 h-[420px] w-[420px] rotate-90 text-white/[0.06]" />
    <div className="mx-auto grid max-w-[1600px] grid-cols-1 items-center gap-10 px-6 lg:grid-cols-12 lg:px-12">
      <div className="lg:col-span-3">
        <Reveal>
          <h2 className="font-serif text-5xl text-white sm:text-6xl">Vistas</h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-8 font-sans text-base leading-relaxed text-white/90">
            These clips showcase a selection of shots I produced for a travel client
            in Australia, focused on promoting self-drive touring experiences across
            the country. The work highlights expansive road journeys, scenic
            landscapes, and the sense of freedom that comes with exploring Australia
            by car. Alongside this, I've included holiday footage captured in Greece,
            featuring coastal views, historic architecture, and everyday moments that
            convey the atmosphere, light, and pace of Mediterranean travel.
          </p>
        </Reveal>
      </div>
      <div className="lg:col-span-9">
        <Reveal delay={0.2}>
          <VideoPlaceholder testId="vistas-video" vimeoId="1212275646" background />
        </Reveal>
      </div>
    </div>
  </section>
);

// ---- Agriculture ----
export const Agriculture = () => {
  const cards = [
    {
      title: "Livestock surveillance",
      body: "Using my drone skills to check on the sheep flock during lambing season.",
    },
    {
      title: "Practical uses for the Agriculture sector",
      body: "Working on things like solar panel surveys, high level machinery checks and livestock reconnaissance.",
    },
  ];
  return (
    <section id="agriculture" className="relative overflow-hidden bg-white py-24 lg:py-32">
      <Swoosh className="left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 text-slate-900/[0.03]" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <h2 className="font-serif text-5xl text-teal-800 sm:text-6xl">Agriculture</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-4xl font-sans text-base leading-relaxed text-slate-600">
            Drones can be really useful on large farms, not just for beautiful footage
            but also for various practical uses. During my time working on a large farm
            in 2023 I recorded and edited this footage in my spare time and it was used
            by the farmer for his own website.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
          {cards.map((c, i) => (
            <Reveal key={c.title} delay={0.1 + i * 0.1}>
              <div
                data-testid={`agri-card-${i}`}
                className="group h-full rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm transition-all duration-500 hover:border-emerald-500/70 hover:shadow-lg"
              >
                <h3 className="font-serif text-2xl text-emerald-600 sm:text-3xl">
                  {c.title}
                </h3>
                <p className="mt-4 font-sans text-base leading-relaxed text-slate-600">
                  {c.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <Reveal delay={0.15} className="mt-16">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <VideoPlaceholder
            testId="agri-video"
            vimeoId="1213905604"
            background
          />
        </div>
      </Reveal>
    </section>
  );
};

// ---- Transport ----
export const Transport = () => (
  <section id="transport" className="relative overflow-hidden bg-white py-24 lg:py-32">
    <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <h2 className="font-serif text-5xl text-teal-800 sm:text-6xl">Transport</h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="mt-6 max-w-4xl font-sans text-base leading-relaxed text-slate-600">
          I film a wide range of transport, from agricultural machinery and cars to
          boats. Depending on the brief, I can capture subjects in a smooth, cinematic
          style or follow them at speed to create high-energy sequences. Using my new
          FPV drone, I'm able to produce dynamic, immersive shots that add a striking
          and modern edge to the content.
        </p>
      </Reveal>
    </div>
    <Reveal delay={0.15} className="mt-14">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <VideoPlaceholder
          testId="transport-video"
          vimeoId="1213931002"
          background
        />
      </div>
    </Reveal>
    <Reveal delay={0.2} className="mt-10">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <VideoPlaceholder
          testId="transport-video-2"
          label="Latest Transport Work"
          vimeoId="1225562400?h=0f6f804e99"
        />
      </div>
    </Reveal>
  </section>
);

// ---- FPV ----
export const FPV = () => {
  const cols = [
    {
      text: "Dynamic FPV drone interior shots designed to highlight properties and businesses from a unique, engaging perspective.",
      vimeoId: "1162199815",
    },
    {
      text: "FPV drone footage capturing smooth exterior fly-throughs for properties and businesses.",
      vimeoId: "1162223872",
    },
    {
      text: "FPV follow-cam videos tailored to your needs, tracking vehicles or subjects of your choice with smooth, dynamic motion.",
      vimeoId: "1163576035",
    },
  ];
  return (
    <section id="fpv" className="relative overflow-hidden bg-white py-24 lg:py-32">
      <Swoosh className="-left-40 bottom-0 h-[500px] w-[500px] text-slate-900/[0.03]" />
      <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
        <Reveal className="text-center">
          <h2 className="font-serif text-4xl text-slate-900 sm:text-5xl lg:text-6xl">
            New FPV Content
          </h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 gap-10 md:grid-cols-3">
          {cols.map((c, i) => (
            <Reveal key={i} delay={i * 0.12}>
              <div data-testid={`fpv-col-${i}`}>
                <VideoPlaceholder
                  testId={`fpv-video-${i}`}
                  label={c.vimeoId ? null : `FPV · 0${i + 1}`}
                  vimeoId={c.vimeoId}
                  background={!!c.vimeoId}
                />
                <div className="mt-6 border-t border-emerald-500/50 pt-6">
                  <span className="font-serif text-4xl text-emerald-600/50">
                    0{i + 1}
                  </span>
                  <p className="mt-4 font-sans text-base leading-relaxed text-slate-600">
                    {c.text}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
